"use client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { convertedPriceKey, useUserPreferencesContext } from "../index";
import { setCurrencyCookie } from "@/lib/currency/cookieUtils";
import {
  getFxQuoteCookie,
  setFxQuoteCookie,
  clearFxQuoteCookie,
  type FxQuoteCookiePayload,
} from "@/lib/currency/fxQuoteCookieUtils";
import { getCurrencySymbol } from "@/lib/currency/currencyUtils";
import { DEFAULT_CURRENCY_SELECTOR_MODAL_CURRENCIES } from "@/components/CurrencySelector/constants";
import { convertCurrencyFXQuoteClient } from "@/components/CurrencySelector/util";
import { lockCurrencyQuoteAction } from "@/app/actions/currency";
import { DEFAULT_CURRENCY } from "@/lib/currency/constants";
import { isAcceptedCurrency } from "@/lib/currency/dualPricingRules";
import { DEFAULT_USER_CURRENCY } from "@/lib/course/utils/constants";
import useFeatureFlag from "@/lib/feature-flags/hooks/useFeatureFlag";
import { MonarchCurrencySelectorProps } from "@/monarch/types/MonarchCurrencySelector";

const QUOTE_REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 min before expiry
const QUOTE_CHECK_INTERVAL_MS = 60 * 1000;

/**
 * Hook for currency functionality: handles currency selection, conversion,
 * locked FX quote lifecycle, and price caching.
 */
export const useCurrency = () => {
  const {
    selectedCurrency,
    setSelectedCurrency,
    convertedPrices,
    setConvertedPrice,
    clearConvertedPrices,
    fxQuote,
    setFxQuote,
  } = useUserPreferencesContext();

  const {
    flagValue: multiCurrencyFlag,
    isLoading: isMultiCurrencyFlagLoading,
  } = useFeatureFlag("NEXT_PUBLIC_FEATURE_MULTI_CURRENCY_ACCEPTANCE", "legacy");
  const isMultiCurrencyEnabled = multiCurrencyFlag === "test";

  const refreshingRef = useRef(false);
  const [conversionFailed, setConversionFailed] = useState<string | null>(null);

  const refreshLockedQuote = useCallback(
    async (currency: string) => {
      if (!isMultiCurrencyEnabled) return;
      if (refreshingRef.current) return;
      refreshingRef.current = true;

      try {
        const quote = await lockCurrencyQuoteAction("USD", currency);
        if (quote) {
          const payload: FxQuoteCookiePayload = {
            currency,
            fxQuoteId: quote.fxQuoteId,
            lockedRate: quote.lockedRate,
            expiresAt: quote.expiresAt,
          };
          setFxQuoteCookie(payload);
          setFxQuote(payload);
        }
      } finally {
        refreshingRef.current = false;
      }
    },
    [setFxQuote, isMultiCurrencyEnabled],
  );

  // Acquire a locked quote on mount when currency is non-USD but no valid quote exists
  // (e.g. cookie expired between sessions)
  useEffect(() => {
    if (!isMultiCurrencyEnabled) return;
    if (selectedCurrency === DEFAULT_CURRENCY) return;
    if (!isAcceptedCurrency(selectedCurrency)) return;
    if (fxQuote?.currency === selectedCurrency) return;

    refreshLockedQuote(selectedCurrency);
  }, [isMultiCurrencyEnabled, selectedCurrency, fxQuote, refreshLockedQuote]);

  // Periodically check quote expiry and silently refresh
  useEffect(() => {
    if (!isMultiCurrencyEnabled) return;
    if (!isAcceptedCurrency(selectedCurrency)) return;

    const checkExpiry = () => {
      const current = getFxQuoteCookie();
      if (!current) return;
      if (current.currency !== selectedCurrency) return;

      const expiresAt = new Date(current.expiresAt).getTime();
      const now = Date.now();

      if (expiresAt - now < QUOTE_REFRESH_BUFFER_MS) {
        refreshLockedQuote(current.currency);
      }
    };

    checkExpiry();
    const interval = setInterval(checkExpiry, QUOTE_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refreshLockedQuote, isMultiCurrencyEnabled, selectedCurrency]);

  const setCurrency = useCallback(
    (currency: string) => {
      setCurrencyCookie(currency);
      setSelectedCurrency(currency);
      clearConvertedPrices();
      setConversionFailed(null);

      if (isMultiCurrencyEnabled) {
        if (isAcceptedCurrency(currency) && currency !== DEFAULT_CURRENCY) {
          refreshLockedQuote(currency);
        } else {
          clearFxQuoteCookie();
          setFxQuote(null);
        }
      }
    },
    [
      setSelectedCurrency,
      clearConvertedPrices,
      setFxQuote,
      refreshLockedQuote,
      isMultiCurrencyEnabled,
    ],
  );

  const getCurrencyData = (code: string) => {
    return DEFAULT_CURRENCY_SELECTOR_MODAL_CURRENCIES.find(
      (c) => c.code === code,
    );
  };

  const currencySymbol = useMemo(
    () => getCurrencySymbol(selectedCurrency),
    [selectedCurrency],
  );

  /**
   * Gets or calculates a converted product price for the selected currency.
   * Converts synchronously via the locked FX rate when one is available for
   * the selected currency; otherwise checks the cache for this exact USD
   * price (keyed by currency + price), or fetches and caches an indicative
   * rate.
   *
   * @param price - The price in USD to convert
   * @param currencySelector - The currency selector configuration from Monarch (optional)
   * @returns Object with convertedPrice, displayCurrency, and isLoading
   */
  const getConvertedProductPrice = useCallback(
    (
      price: number | null | undefined,
      currencySelector?: MonarchCurrencySelectorProps | null,
    ) => {
      if (!currencySelector || !price || price <= 0) {
        return {
          convertedPrice: price,
          displayCurrency: DEFAULT_USER_CURRENCY,
          isLoading: false,
        };
      }

      if (selectedCurrency === DEFAULT_CURRENCY) {
        return {
          convertedPrice: price,
          displayCurrency: DEFAULT_USER_CURRENCY,
          isLoading: false,
        };
      }

      const currencyData = getCurrencyData(selectedCurrency);

      if (!currencyData) {
        return {
          convertedPrice: price,
          displayCurrency: DEFAULT_USER_CURRENCY,
          isLoading: false,
        };
      }

      // The multi-currency flag resolves asynchronously. While it's loading,
      // `isMultiCurrencyEnabled` reads as false, which would otherwise trigger
      // an indicative-rate fetch that gets thrown away the instant the flag
      // resolves to "test" and a locked rate takes over -- a visible flicker
      // from the fetched amount to the locked-rate amount. Hold in a loading
      // state until the flag settles instead.
      if (isMultiCurrencyFlagLoading) {
        return {
          convertedPrice: price,
          displayCurrency: {
            code: currencyData.code,
            rate: 1.0,
            symbol: currencyData.symbol,
          },
          isLoading: true,
        };
      }

      // Convert with the locked FX rate before consulting the shared cache.
      // The locked rate is per-currency, so `price * lockedRate` is correct
      // for any USD amount, and doing this first means a different price in
      // the same currency can never reuse another surface's cached
      // conversion for a different amount.
      const lockedRate =
        isMultiCurrencyEnabled && fxQuote?.currency === selectedCurrency
          ? fxQuote.lockedRate
          : null;

      if (lockedRate != null && lockedRate > 0) {
        return {
          convertedPrice: price * lockedRate,
          displayCurrency: {
            code: currencyData.code,
            rate: lockedRate,
            symbol: currencyData.symbol,
          },
          isLoading: false,
        };
      }

      // No locked rate yet: fall back to the cached conversion for this exact
      // currency + USD price, or fetch an indicative rate. The cache is keyed
      // by (currency, sourcePrice) rather than a single shared slot per
      // currency, so multiple different USD prices rendered at once in the
      // same currency (e.g. a discounted price and a full list price on the
      // same page) each get their own entry instead of repeatedly evicting
      // one another's cached amount and re-fetching in a loop.
      const cached =
        convertedPrices[convertedPriceKey(selectedCurrency, price)];

      if (cached) {
        return {
          convertedPrice: cached.price,
          displayCurrency: {
            code: currencyData.code,
            rate: 1.0,
            symbol: currencyData.symbol,
          },
          isLoading: false,
        };
      }

      if (conversionFailed === selectedCurrency) {
        return {
          convertedPrice: price,
          displayCurrency: DEFAULT_USER_CURRENCY,
          isLoading: false,
        };
      }

      convertCurrencyFXQuoteClient(price, selectedCurrency as any, lockedRate)
        .then((apiConverted) => {
          if (apiConverted !== null) {
            setConvertedPrice({
              price: apiConverted,
              currencyCode: selectedCurrency,
              sourcePrice: price,
            });
          } else {
            setConversionFailed(selectedCurrency);
          }
        })
        .catch(() => {
          setConversionFailed(selectedCurrency);
        });

      return {
        convertedPrice: price,
        displayCurrency: {
          code: currencyData.code,
          rate: lockedRate ?? 1.0,
          symbol: currencyData.symbol,
        },
        isLoading: true,
      };
    },
    [
      selectedCurrency,
      convertedPrices,
      setConvertedPrice,
      fxQuote,
      isMultiCurrencyEnabled,
      isMultiCurrencyFlagLoading,
      conversionFailed,
    ],
  );

  return {
    selectedCurrency,
    currencySymbol,
    setCurrency,
    getCurrencyData,
    convertedPrices,
    setConvertedPrice,
    getConvertedProductPrice,
    fxQuote,
    refreshLockedQuote,
  };
};
